import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError, CreateSopInput } from '@pms/shared';
import { createSopSchema, SOP_STATUSES } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

const NONE = '__none__';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  sopId: string | null; // null => create
  onClose: () => void;
}

export function SopFormDialog({ sopId, onClose }: Props) {
  const isEdit = !!sopId;
  const queryClient = useQueryClient();

  const categoriesQuery = useQuery({
    queryKey: ['sop', 'categories'],
    queryFn: () => sopService.listCategories(),
  });

  const detailQuery = useQuery({
    queryKey: ['sop', 'sop', sopId],
    queryFn: () => sopService.getSop(sopId!),
    enabled: isEdit,
  });

  const { register, handleSubmit, control, reset, formState } = useForm<CreateSopInput>({
    resolver: zodResolver(createSopSchema),
    defaultValues: { title: '', category_id: null, summary: '', content: '', status: 'draft' },
  });

  useEffect(() => {
    if (isEdit && detailQuery.data) {
      const d = detailQuery.data;
      reset({
        title: d.title,
        category_id: d.category_id,
        summary: d.summary ?? '',
        content: d.content,
        status: d.status,
      });
    }
  }, [isEdit, detailQuery.data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: CreateSopInput): Promise<void> => {
      if (isEdit) await sopService.updateSop(sopId!, values);
      else await sopService.createSop(values);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'SOP updated' : 'SOP created');
      void queryClient.invalidateQueries({ queryKey: ['sop', 'sops'] });
      void queryClient.invalidateQueries({ queryKey: ['sop', 'stats'] });
      if (isEdit) void queryClient.invalidateQueries({ queryKey: ['sop', 'sop', sopId] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const loadingDetail = isEdit && detailQuery.isLoading;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit SOP' : 'Create SOP'}</DialogTitle>
        </DialogHeader>

        {loadingDetail ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sop-title">Title *</Label>
              <Input id="sop-title" {...register('title')} placeholder="e.g. Leave Application Process" />
              {formState.errors.title && <p className="text-xs text-destructive">{formState.errors.title.message}</p>}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Controller
                  control={control}
                  name="category_id"
                  render={({ field }) => (
                    <Select
                      value={field.value ?? NONE}
                      onValueChange={(v) => field.onChange(v === NONE ? null : v)}
                    >
                      <SelectTrigger><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Uncategorized</SelectItem>
                        {(categoriesQuery.data ?? []).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SOP_STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sop-summary">Summary</Label>
              <Textarea id="sop-summary" {...register('summary')} placeholder="Short description shown on cards" rows={2} />
            </div>

            <div className="space-y-1.5">
              <Label>Content</Label>
              <Controller
                control={control}
                name="content"
                render={({ field }) => (
                  <RichTextEditor value={field.value ?? ''} onChange={field.onChange} />
                )}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
